import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/testIds";
import { useT } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

const IMAGES = [
    "https://images.unsplash.com/photo-1772207112257-88d9566423d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxpdGFsaWFuJTIwc3RyZWV0JTIwY2l0eSUyMHRyYWZmaWN8ZW58MHx8fHwxNzg0NzIyNjg4fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1589578527966-fdac0f44566c?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1552667466-07770ae110d0?auto=format&fit=crop&w=1200&q=80",
];

export default function Coverage() {
    const { t } = useT();
    const codes = t("coverage.items") || [];
    const [active, setActive] = useState(0);
    return (
        <section
            id="codici"
            data-testid={TID.coverage.root}
            className="bg-sn-paper py-24 md:py-40 relative overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("coverage.eyebrow")}
                    </span>
                    <span className="hairline flex-1" />
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-ink-2">
                        {t("coverage.count")}
                    </span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl text-sn-ink"
                >
                    {t("coverage.headline_a")}{" "}
                    <em className="italic text-sn-blue">
                        {t("coverage.headline_b")}
                    </em>
                    .
                </motion.h2>

                <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <ul className="lg:col-span-8 divide-y divide-black/10">
                        {codes.map((c, i) => (
                            <li key={i}>
                                <button
                                    data-testid={TID.coverage.item(i + 1)}
                                    onMouseEnter={() => setActive(i)}
                                    onFocus={() => setActive(i)}
                                    onClick={() => setActive(i)}
                                    className="w-full text-left py-8 md:py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3 group transition-colors duration-300"
                                >
                                    <div className="flex items-center gap-6">
                                        <span
                                            className={`font-mono-e text-xs tracking-[0.3em] transition-colors duration-300 ${active === i ? "text-sn-blue" : "text-sn-ink-2"}`}
                                        >
                                            0{i + 1}
                                        </span>
                                        <span
                                            className={`font-serif text-6xl md:text-8xl leading-none tracking-tight transition-colors duration-500 ${active === i ? "text-sn-ink" : "text-sn-ink/40 group-hover:text-sn-ink"}`}
                                        >
                                            {c.name}
                                        </span>
                                    </div>
                                    <span className="font-mono-e text-xs text-sn-ink-2 max-w-xs uppercase tracking-widest md:text-right">
                                        {c.example}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="lg:col-span-4 lg:sticky lg:top-28">
                        <div className="relative aspect-[4/5] w-full rounded-sm overflow-hidden bg-sn-black">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={active}
                                    initial={{ opacity: 0, scale: 1.05, clipPath: "inset(0 0 100% 0)" }}
                                    animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0 0% 0)" }}
                                    exit={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
                                    transition={{ duration: 0.8, ease: EASE }}
                                    className="absolute inset-0 duotone"
                                >
                                    <img
                                        src={IMAGES[active]}
                                        alt={codes[active] && codes[active].name}
                                        className="w-full h-full object-cover"
                                    />
                                </motion.div>
                            </AnimatePresence>
                            <div className="absolute bottom-0 left-0 right-0 p-5 z-10 text-sn-silver">
                                <div className="font-mono-e text-[10px] tracking-[0.3em] text-sn-fog uppercase">
                                    {t("coverage.previewLabel")}
                                </div>
                                <div className="font-serif italic text-2xl mt-1">
                                    {codes[active] && codes[active].name.toLowerCase()}
                                </div>
                            </div>
                            <div className="absolute top-0 left-0 right-0 flex justify-between p-4 z-10 font-mono-e text-[9px] tracking-widest text-sn-silver/80 uppercase">
                                <span>0{active + 1} / 04</span>
                                <span>{t("coverage.anteprima")}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
